import { useState } from 'react';
import NextImage, { ImageProps as NextImageProps, ImageLoader } from 'next/image';

import { PlaceholderImage } from '@phoenix/core/frontend/shared/ui/images/placeholder-image';

interface ImageProps extends Omit<NextImageProps, 'src'> {
  src: string;
}

export const Image = ({ src, ...props }: ImageProps) => {
  const [isError, setIsError] = useState(false);
  const isFromContentful = isContentfulImage(src);

  const imageLoader: () => ImageLoader | undefined = () => {
    if (isFromContentful) {
      return contentfulImageLoader;
    }

    return () => src;
  };

  return isError ? (
    <div className="flex h-full w-full items-center justify-center bg-secondary-light opacity-50">
      <PlaceholderImage className="h-8 text-secondary" />
    </div>
  ) : (
    <NextImage loader={imageLoader()} unoptimized={!isFromContentful} {...props} src={src} onError={() => setIsError(true)} />
  );
};

function contentfulImageLoader({ src, width, quality = 75 }): string {
  try {
    const url = new URL(src);
    url.searchParams.set('fm', 'jpg');
    url.searchParams.set('fl', 'progressive');
    url.searchParams.set('w', width.toString());
    url.searchParams.set('q', quality.toString());
    return url.href;
  } catch {
    return src;
  }
}

function isContentfulImage(url: string) {
  try {
    return /^images(\.\w{2})?\.ctfassets\.net$/.test(new URL(url).hostname);
  } catch {
    return false;
  }
}
